import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import agreementService from '../services/agreementService';

const AgreementsPage = () => {
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState({ type: '', signed: '' });
  const [selectedAgreement, setSelectedAgreement] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    loadAgreements();
  }, [filter]);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape' && showModal) {
        setShowModal(false);
      }
    };

    window.addEventListener('keydown', handleEscapeKey);
    return () => window.removeEventListener('keydown', handleEscapeKey);
  }, [showModal]);

  const loadAgreements = async () => {
    try {
      setLoading(true);
      const response = await agreementService.getAgreements(filter);
      setAgreements(response.data?.data || []);
      setError(null);
    } catch (err) {
      console.error('Error loading agreements:', err);
      console.error('Error response:', err.response?.data);
      const errorMsg = err.response?.data?.message || err.message || 'Failed to load agreements';
      setError(`Failed to load agreements: ${errorMsg}`);
      setAgreements([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const handleViewAgreement = (agreement) => {
    setSelectedAgreement(agreement);
    setShowModal(true);
  };

  const handleDownloadAgreement = async (agreement) => {
    try {
      await agreementService.downloadAgreement(agreement.id, agreement);
    } catch (err) {
      console.error('Download error:', err);
      alert('Failed to download agreement');
    }
  };

  const stats = {
    total: agreements.length,
    signed: agreements.filter(a => a.signed_at || a.is_signed).length,
    pending: agreements.filter(a => !a.signed_at && !a.is_signed).length,
    hostel: agreements.filter(a => a.agreement_type === 'hostel').length,
    mess: agreements.filter(a => a.agreement_type === 'mess').length,
  };

  return (
    <Layout title="Agreements">
      <style>{`
        .filter-row { display: flex; gap: 12px; margin-bottom: 24px; }
        .filter-select { padding: 10px 14px; border: 1px solid rgba(0, 0, 0, 0.04); border-radius: 12px; font-size: 14px; min-width: 140px; background: white; box-shadow: 0 4px 8px -2px rgba(99, 102, 241, 0.1), 0 6px 12px -4px rgba(0, 0, 0, 0.05); transition: all 0.2s ease; }
        .filter-select:focus { outline: none; border-color: #84cc16; box-shadow: 0 6px 12px -2px rgba(132, 204, 22, 0.2), 0 10px 20px -5px rgba(0, 0, 0, 0.06); }
        .stats-row { display: flex; gap: 20px; margin-bottom: 24px; }
        .stat-card { background: white; padding: 20px; border-radius: 16px; border: 1px solid rgba(0, 0, 0, 0.04); flex: 1; box-shadow: 0 6px 12px -2px rgba(79, 70, 229, 0.15), 0 10px 20px -5px rgba(0, 0, 0, 0.05); transition: all 0.2s ease; }
        .stat-card:hover { transform: translateY(-2px); box-shadow: 0 10px 20px -4px rgba(79, 70, 229, 0.2), 0 15px 25px -8px rgba(0, 0, 0, 0.08); }
        .stat-card h3 { font-size: 12px; color: #6b7280; margin: 0 0 8px; text-transform: uppercase; }
        .stat-card .value { font-size: 28px; font-weight: 600; color: #1f2937; }
        .stat-card .value.green { color: #047857; }
        .stat-card .value.yellow { color: #b45309; }
        .data-table { width: 100%; border-collapse: collapse; }
        .data-table th { background: #f9fafb; padding: 12px 16px; text-align: left; font-size: 12px; font-weight: 600; text-transform: uppercase; color: #6b7280; border-bottom: 1px solid #e5e7eb; }
        .data-table td { padding: 14px 16px; border-bottom: 1px solid #f3f4f6; color: #374151; font-size: 14px; }
        .data-table tr:hover { background: #f9fafb; }
        .btn { padding: 6px 12px; border: none; border-radius: 6px; cursor: pointer; font-weight: 500; font-size: 13px; }
        .btn-primary { background: #f7fee7; color: #65a30d; }
        .btn-success { background: #d1fae5; color: #047857; }
        .btn-secondary { background: #f3f4f6; color: #374151; }
        .status-badge { padding: 4px 12px; border-radius: 12px; font-size: 12px; font-weight: 500; }
        .status-signed { background: #d1fae5; color: #047857; }
        .status-pending { background: #fef3c7; color: #b45309; }
        .type-badge { padding: 4px 10px; border-radius: 6px; font-size: 12px; font-weight: 500; }
        .type-hostel { background: #e0e7ff; color: #4338ca; }
        .type-mess { background: #fef3c7; color: #b45309; }
        .actions { display: flex; gap: 8px; }
        .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; }
        .modal { background: white; border-radius: 16px; width: 100%; max-width: 560px; max-height: 90vh; overflow-y: auto; box-shadow: 0 20px 40px -10px rgba(99, 102, 241, 0.25), 0 30px 50px -15px rgba(0, 0, 0, 0.15); border: 1px solid rgba(0, 0, 0, 0.04); }
        .modal-header { padding: 20px 24px; border-bottom: 1px solid #e5e7eb; display: flex; justify-content: space-between; align-items: center; }
        .modal-header h2 { margin: 0; font-size: 18px; font-weight: 600; }
        .modal-close { background: none; border: none; font-size: 24px; cursor: pointer; color: #6b7280; }
        .modal-body { padding: 24px; }
        .modal-footer { padding: 16px 24px; border-top: 1px solid #e5e7eb; display: flex; justify-content: flex-end; gap: 12px; }
        .detail-row { display: flex; margin-bottom: 12px; }
        .detail-label { width: 120px; font-weight: 500; color: #6b7280; font-size: 14px; }
        .detail-value { flex: 1; color: #1f2937; font-size: 14px; }
        .content-box { background: #f9fafb; padding: 16px; border-radius: 8px; margin-top: 16px; white-space: pre-wrap; font-size: 14px; }
        .loading, .error, .empty { text-align: center; padding: 48px; color: #6b7280; }
        .error { color: #dc2626; }
      `}</style>

      <div className="filter-row">
        <select className="filter-select" value={filter.type} onChange={(e) => setFilter({ ...filter, type: e.target.value })}>
          <option value="">All Types</option>
          <option value="hostel">Hostel</option>
          <option value="mess">Mess</option>
        </select>
        <select className="filter-select" value={filter.signed} onChange={(e) => setFilter({ ...filter, signed: e.target.value })}>
          <option value="">All Status</option>
          <option value="true">Signed</option>
          <option value="false">Pending</option>
        </select>
      </div>

      <div className="stats-row">
        <div className="stat-card">
          <h3>Total Agreements</h3>
          <div className="value">{stats.total}</div>
        </div>
        <div className="stat-card">
          <h3>Signed</h3>
          <div className="value green">{stats.signed}</div>
        </div>
        <div className="stat-card">
          <h3>Pending</h3>
          <div className="value yellow">{stats.pending}</div>
        </div>
        <div className="stat-card">
          <h3>Hostel</h3>
          <div className="value">{stats.hostel}</div>
        </div>
        <div className="stat-card">
          <h3>Mess</h3>
          <div className="value">{stats.mess}</div>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading agreements...</div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : agreements.length === 0 ? (
        <div className="empty">No agreements found</div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Student</th>
              <th>Type</th>
              <th>Created</th>
              <th>Signed</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {agreements.map((agreement) => (
              <tr key={agreement.id}>
                <td>#{agreement.id}</td>
                <td><strong>{agreement.student?.user?.first_name} {agreement.student?.user?.last_name}</strong></td>
                <td><span className={`type-badge type-${agreement.agreement_type}`}>{agreement.agreement_type}</span></td>
                <td>{formatDate(agreement.created_at)}</td>
                <td>{formatDate(agreement.signed_at) || '-'}</td>
                <td>
                  <span className={`status-badge ${agreement.signed_at || agreement.is_signed ? 'status-signed' : 'status-pending'}`}>
                    {agreement.signed_at || agreement.is_signed ? 'Signed' : 'Pending'}
                  </span>
                </td>
                <td>
                  <div className="actions">
                    <button className="btn btn-primary" onClick={() => handleViewAgreement(agreement)}>View</button>
                    <button className="btn btn-success" onClick={() => handleDownloadAgreement(agreement)}>Download</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showModal && selectedAgreement && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Agreement Details</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="detail-row">
                <span className="detail-label">Agreement ID:</span>
                <span className="detail-value">#{selectedAgreement.id}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Student:</span>
                <span className="detail-value">{selectedAgreement.student?.user?.first_name} {selectedAgreement.student?.user?.last_name}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Type:</span>
                <span className="detail-value"><span className={`type-badge type-${selectedAgreement.agreement_type}`}>{selectedAgreement.agreement_type}</span></span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Created:</span>
                <span className="detail-value">{formatDate(selectedAgreement.created_at)}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Signed:</span>
                <span className="detail-value">{formatDate(selectedAgreement.signed_at) || 'Not signed yet'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Status:</span>
                <span className="detail-value">
                  <span className={`status-badge ${selectedAgreement.signed_at || selectedAgreement.is_signed ? 'status-signed' : 'status-pending'}`}>
                    {selectedAgreement.signed_at || selectedAgreement.is_signed ? 'Signed' : 'Pending'}
                  </span>
                </span>
              </div>
              {selectedAgreement.content && (
                <div>
                  <div className="detail-label" style={{ marginTop: '16px', marginBottom: '8px' }}>Agreement Content:</div>
                  <div className="content-box">{selectedAgreement.content}</div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-success" onClick={() => handleDownloadAgreement(selectedAgreement)}>Download PDF</button>
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default AgreementsPage;
